import * as _ from 'lodash-es';
import { Delta, Op } from 'quill/core';
import VersionChangeFormat from '../../editor/formats/VersionChange';
import type { TableChange, TableChangeCell } from '../../editor/table/types';

const getUniqueId = () => _.uniqueId('version-change-');

export type VersionChange = {
  id: string;
  version: number;
  authorId: string;
};

/**
 * A version change is defined as a series of consecutive changes made by the same author.
 * Only "inserted" and "deleted" are taken into account, whereas "formatted" changes are ignored.
 */
const calculateVersionChanges = (diff: Delta) => {
  const versionChanges: VersionChange[] = [];
  let delta = new Delta();

  let current: VersionChange | null = null;

  diff.forEach((op) => {
    const length = Op.length(op);
    let opDelta = new Delta().retain(length);

    const { version, diff: diffAttr, author: authorId } = op.attributes || {};
    if (
      (diffAttr === 'inserted' || diffAttr === 'deleted') &&
      typeof authorId === 'string' &&
      typeof version === 'number'
    ) {
      if (current?.authorId !== authorId || current.version !== version) {
        const versionChange: VersionChange = {
          id: getUniqueId(),
          authorId,
          version,
        };
        versionChanges.push(versionChange);
        current = versionChange;
      }
      opDelta = opDelta.compose(
        new Delta().retain(length, {
          [VersionChangeFormat.attrName]: current.id,
        }),
      );
    } else {
      current = null;
    }

    const data = op.insert || op.retain;
    if (data && typeof data === 'object') {
      if ('code-embed' in data) {
        const { delta: subDelta, versionChanges: subVersionChanges } =
          calculateVersionChanges(data['code-embed'] as Delta);
        versionChanges.push(...subVersionChanges);
        opDelta = opDelta.compose(
          new Delta().retain({ 'code-embed': subDelta.ops }),
        );
      } else if ('table-embed' in data) {
        const cellChanges: Record<string, TableChangeCell> = {};
        const { cells } = data['table-embed'] as TableChange;
        if (cells && typeof cells === 'object') {
          Object.keys(cells).forEach((identity) => {
            const content = cells[identity]?.content;
            if (Array.isArray(content)) {
              const { delta: subDelta, versionChanges: subVersionChanges } =
                calculateVersionChanges(new Delta(content));
              versionChanges.push(...subVersionChanges);
              cellChanges[identity] = { content: subDelta.ops };
            }
          });
        }
        if (Object.keys(cellChanges).length) {
          opDelta = opDelta.compose(
            new Delta().retain({ 'table-embed': { cells: cellChanges } }),
          );
        }
      }
    }

    delta = delta.concat(opDelta);
  });

  return { delta: delta.chop(), versionChanges };
};

export default calculateVersionChanges;
